/**
 * YearEndGalleryCard.js
 *
 * Card component for displaying a user's year-end review in the gallery
 * Shows avatar, username, total views, and thread count
 */

import React, { useMemo } from 'react';
import { Box, Typography, Avatar, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { IconEye, IconPencil, IconHeart } from '@tabler/icons-react';
import { getPublicAvatarUrl } from '../../utils/publicAvatarUtils';

/**
 * Year-End Gallery Card
 *
 * @param {Object} props
 * @param {Object} props.yearEndData - yearEnd document data from Firestore
 */
export function YearEndGalleryCard({ yearEndData }) {
  const navigate = useNavigate();

  const {
    id,
    username = '',
    userId,
    profileImage,
    stats = {}
  } = yearEndData || {};

  // Avatar URL with fallback to Firebase Storage
  const avatarUrl = useMemo(() => {
    if (profileImage) return profileImage;
    if (userId) return getPublicAvatarUrl(userId, 160);
    return null;
  }, [profileImage, userId]);

  // Generate fallback color from username
  const fallbackColor = useMemo(() => {
    if (!username) return '#e0e0e0';
    const hash = username.split('').reduce((acc, char) => {
      return char.charCodeAt(0) + ((acc << 5) - acc);
    }, 0);
    const hue = Math.abs(hash) % 360;
    return `hsl(${hue}, 60%, 75%)`;
  }, [username]);

  const handleClick = () => {
    if (id) {
      navigate(`/year-end/${id}`);
    }
  };

  // Stats from yearEnd document
  const totalThreads = stats?.totalThreads || 0;
  const totalViews = stats?.totalViews || 0;
  const totalLikes = stats?.totalLikes || 0;

  return (
    <Box
      onClick={handleClick}
      sx={{
        p: 2,
        cursor: 'pointer',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        transition: 'all 0.2s ease',
        '&:hover': {
          bgcolor: 'primary.main',
          borderColor: 'primary.main',
          transform: 'translateY(-2px)',
          boxShadow: 2,
          '& .MuiTypography-root': {
            color: 'white !important'
          },
          '& .stat-icon': {
            color: 'white !important'
          }
        }
      }}
    >
      {/* Avatar */}
      <Avatar
        src={avatarUrl}
        alt={username}
        sx={{
          width: 64,
          height: 64,
          mb: 1.5,
          bgcolor: fallbackColor,
          fontSize: '1.5rem',
          fontWeight: 600
        }}
      >
        {username ? username.charAt(0).toUpperCase() : '?'}
      </Avatar>

      {/* Username */}
      <Typography
        variant="subtitle2"
        fontWeight={700}
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          width: '100%',
          mb: 1
        }}
      >
        @{username}
      </Typography>

      {/* Stats Row */}
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        justifyContent="center"
        sx={{ mt: 'auto' }}
      >
        {/* Total Views */}
        <Stack direction="row" spacing={0.5} alignItems="center">
          <IconEye
            size={14}
            className="stat-icon"
            style={{ color: 'var(--mantine-color-gray-6)' }}
          />
          <Typography variant="caption" color="text.secondary">
            {totalViews.toLocaleString()}
          </Typography>
        </Stack>

        {/* Thread Count */}
        <Stack direction="row" spacing={0.5} alignItems="center">
          <IconPencil
            size={14}
            className="stat-icon"
            style={{ color: 'var(--mantine-color-gray-6)' }}
          />
          <Typography variant="caption" color="text.secondary">
            {totalThreads.toLocaleString()}
          </Typography>
        </Stack>

        {/* Total Likes */}
        <Stack direction="row" spacing={0.5} alignItems="center">
          <IconHeart
            size={14}
            className="stat-icon"
            style={{ color: 'var(--mantine-color-gray-6)' }}
          />
          <Typography variant="caption" color="text.secondary">
            {totalLikes.toLocaleString()}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
}

export default YearEndGalleryCard;
